import { ProductDetailDownloadType } from '@core/enums';
import { gtm } from '@helpers/dataLayer';
import { Device, until } from '@helpers/media';
import { STATIC_IDS } from '@helpers/staticIds';
import withRedux from '@helpers/withRedux';
import useStackAdapt from '@hooks/useStackAdapt';
import { useWindowCustomEvents } from '@hooks/useWindowCustomEvents';
import Pin from '@img/icons/drain-cleaners/pin.svg';
import Warning from '@img/icons/warning.svg';
import RecyclingD from '@img/recycling/D.png';
import RecyclingN from '@img/recycling/N.png';
import RecyclingQ from '@img/recycling/Q.png';
import RecyclingY from '@img/recycling/Y.png';
import { RootState } from '@state/ducks';
import { ProductDetailAction, SET_SELECTED_VARIANT } from '@state/ducks/productdetail/productdetail.reducer';
import LasersPopupForm, {
  LasersPopupFormProps,
} from '@stories/Components/Forms/LasersPopupForm/LasersPopupForm.hybrid';
import Button from '@stories/Elements/Forms/Buttons/Button';
import Link from '@stories/Elements/Forms/Buttons/Link';
import NewIcon from '@stories/Elements/Misc/NewIcon/NewIcon';
import RatingStars from '@stories/Elements/Misc/RatingStars/RatingStars';
import Click2Buy from '@stories/Widgets/ProductDetail/Click2Buy/Click2Buy';
import { useVariantDownloads } from '@stories/Widgets/ProductDetail/Downloads/Downloads.api';
import { VariantDownload } from '@stories/Widgets/ProductDetail/Downloads/Downloads.hybrid';
import ProductDocuments from '@stories/Widgets/ProductDetail/ProductDocuments/ProductDocuments';
import { ProductMediaItem } from '@stories/Widgets/ProductDetail/ProductMediaLayout/ProductMediaLayout';
import ProductMediaMobile from '@stories/Widgets/ProductDetail/ProductMediaMobile/ProductMediaMobile';
import ProductStandardsFeatures from '@stories/Widgets/ProductDetail/ProductStandardsFeatures/ProductStandardsFeatures';
import { useProductStandardsFeaturesIcons } from '@stories/Widgets/ProductDetail/ProductStandardsFeatures/ProductStandardsFeatures.api';
import { RangeCta } from '@stories/Widgets/ProductDetail/ProductToolOverview/ProductToolOverview';
import { useWhatsIncluded } from '@stories/Widgets/ProductDetail/WhatsIncluded/WhatsIncluded.api';
import ImageTransform from '@tti/image-transform';
import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import TryThemOn from '../../../Widgets/ProductDetail/TryThemOn/TryThemOn';
import ProductBreakdownAccordion from '../ProductBreakdownAccordion/ProductBreakdownAccordion';
import ProductDetailsContentAccordion from '../ProductDetailsContentAccordion/ProductDetailsContentAccordion';
import ProductDetailsContentVariantSelector, {
  VariantSelectionLayout,
} from '../ProductDetailsContentVariantSelector/ProductDetailsContentVariantSelector';
import { ProductDetailsContentStyles as S } from './ProductDetailsContent.styles';

export interface ProductDetailsContentProps extends BaseProps {
  product: ProductViewModel;
  technologies: ProductMediaItem[] | null;
  customIcons: ProductMediaItem[] | null;
  recyclingIcon?: string[];
  recyclingIconLink?: string;
  translations: Record<string, string | undefined>;
  storeLocatorUrl: string;
  storeLocatorText: string;
  variantSelectionLayout: VariantSelectionLayout;
  showAR: boolean;
  showThreekitConfigurator: boolean;
  warrantyLogo: ProductMediaItem[] | null;
  standardsUrl?: string;
  featuresUrl?: string;
  repairabilityIndexImageUrl?: string;
  repairabilityIndexLinkUrl?: string;
  rangeCta?: RangeCta;
  formCta?: RangeCta;
  popupData: Omit<LasersPopupFormProps, 'pageContext' | 'isOpen' | 'onClose'>;
  c2bAction: string | null;
}

enum RecyclingIcon {
  D = 'D',
  Y = 'Y',
  N = 'N',
  Q = 'Q',
}

const ProductDetailsContent: FunctionComponent<ProductDetailsContentProps> = (props) => {
  useWhatsIncluded();
  useProductStandardsFeaturesIcons();
  useVariantDownloads();
  const windowCustomEvents = useWindowCustomEvents();

  const [isOpen, setIsOpen] = React.useState<boolean>(props.formCta?.openFormByDefault || false);
  const selectedVariantId = useSelector((x: RootState) => x.productDetail.selectedVariantAgilityId);
  const variants = useSelector((x: RootState) => x.productDetail.variants);
  const categories = useSelector((x: RootState) => x.productDetail.categories);
  const selectedVariant = useMemo(() => variants.find((x) => x.agilityId === selectedVariantId), [selectedVariantId]);
  const translations = useSelector((x: RootState) => x.productDetail.translations);
  const includedProducts = useSelector((x: RootState) => x.productDetail.includedProducts);
  const reviewBreakdown = useSelector((x: RootState) => x.productDetail.reviewBreakdown);
  const threeKitConfiguratorUrl = useSelector(
    (rootState: RootState) => rootState.productDetail.threeKitConfiguratorUrl,
  );
  const threeKitConfiguratorButtonText = useSelector(
    (rootState: RootState) => rootState.productDetail.threeKitConfiguratorButtonText,
  );
  const icons = useSelector((x: RootState) => x.productDetail.standardsFeaturesIcons);
  const documents = useSelector<RootState, VariantDownload[] | null | undefined>((x) =>
    x.productDetail.downloads?.filter(
      (x) =>
        x.downloadType === ProductDetailDownloadType.Certificate ||
        x.downloadType === ProductDetailDownloadType.Guide ||
        (x.downloadType === ProductDetailDownloadType.SoftwareUpdate && x.includeInHeroSection) ||
        x.downloadType === ProductDetailDownloadType.SizeGuide,
    ),
  );

  const promotions = useSelector((x: RootState) =>
    x.productDetail.promotions?.filter((p) => p.promotionVariants?.includes(selectedVariantId)),
  );

  const determineActiveTab = (): number => {
    // In order of the tabs below //
    if (icons?.features && icons.features.length > 0) {
      return 0;
    }

    if (icons?.standards && icons.standards.length > 0) {
      return 3;
    }

    if (documents && documents.length > 0) {
      return 2;
    }

    if (icons?.care && icons?.care.length > 0) {
      return 1;
    }

    // Shouldn't reach here, but default to features anyway //
    return 0;
  };

  const dispatch = useDispatch();
  const [interactedWithStoreLocator, setInteractedWithStoreLocator] = useState<boolean>(false);
  const [ctaState, setCtaState] = useState<'unknown' | 'active-c2b' | 'active-sl'>('unknown');
  const [iconTab, setIconTab] = useState<number>(determineActiveTab());
  const { pushConversion } = useStackAdapt();

  const changedSelectedVariant = async (agilityId: number): Promise<void> => {
    if (selectedVariantId === agilityId) {
      return;
    }

    await dispatch<ProductDetailAction>({ type: SET_SELECTED_VARIANT, payload: agilityId });
  };

  useEffect(() => {
    if (window.c2bWidget) {
      window.c2bWidget.init();
    }

    if (selectedVariant && interactedWithStoreLocator === true) {
      setInteractedWithStoreLocator(false);
    }
  }, [selectedVariantId]);

  const handleTabClick = (index: number): void => {
    if (iconTab === index && window.matchMedia(until(Device.Tablet)).matches) {
      setIconTab(-1);
    } else {
      setIconTab(index);
    }
  };

  const getRecyclingIcon = (icon: string): string => {
    switch (icon) {
      case RecyclingIcon.D:
        return RecyclingD;
      case RecyclingIcon.Y:
        return RecyclingY;
      case RecyclingIcon.N:
        return RecyclingN;
      case RecyclingIcon.Q:
        return RecyclingQ;
      default:
        return '';
    }
  };

  let showNewTag = false;
  let newTagText = '';

  if (selectedVariant && selectedVariant.isComingSoon) {
    showNewTag = true;
    newTagText = 'custom.comingsoon';
  } else if (selectedVariant && selectedVariant.isNew) {
    showNewTag = true;
    newTagText = 'custom.new';
  }

  return (
    <S.Container>
      <S.Tags>
        {showNewTag ? <NewIcon text={props.translations[newTagText]} theme="whiteOnRed" /> : null}

        {props.product.systemLogo && props.product.systemLogo.whiteBg && (
          <>
            {props.product.systemLogo.url ? (
              <S.SystemLink
                href={props.product.systemLogo.url}
                target={props.product.systemLogo.openInNewTab ? '_blank' : '_self'}
              >
                <img
                  src={props.product.systemLogo.whiteBg}
                  loading="lazy"
                  alt={props.product.systemLogo.description ? props.product.systemLogo.description : undefined}
                />
              </S.SystemLink>
            ) : (
              <img
                src={props.product.systemLogo.whiteBg}
                loading="lazy"
                alt={props.product.systemLogo.description ? props.product.systemLogo.description : undefined}
              />
            )}
          </>
        )}
      </S.Tags>

      <S.Section>
        <S.H1>{props.product.title}</S.H1>

        <S.Reviews href={`?openreviews=1#${STATIC_IDS.PRODUCT_DETAIL.REVIEWS}`}>
          {reviewBreakdown && reviewBreakdown.totalReviews > 0 ? (
            <>
              <RatingStars averageReviewRating={reviewBreakdown.rawRatingAverage} />
              <span>
                <b>{reviewBreakdown.average}</b>
              </span>
              <span>
                ({reviewBreakdown.totalReviews} {props.translations['custom.reviews.reviews']})
              </span>
            </>
          ) : null}
          <p data-cta-text>{props.translations['custom.reviews.writeareview']}</p>
        </S.Reviews>
      </S.Section>

      <S.Section>
        <S.Mobile>
          <ProductMediaMobile selectedVariantId={selectedVariantId} />
        </S.Mobile>
      </S.Section>

      <ProductDetailsContentVariantSelector
        translations={props.translations}
        selectedVariant={selectedVariant}
        variants={variants}
        layout={props.variantSelectionLayout}
        onVariantSelected={changedSelectedVariant}
      />

      {props.showAR ? (
        <S.Section>
          <TryThemOn translations={props.translations} />
        </S.Section>
      ) : null}

      {threeKitConfiguratorUrl && threeKitConfiguratorButtonText ? (
        <S.Section>
          <Link text={threeKitConfiguratorButtonText} href={threeKitConfiguratorUrl} />
        </S.Section>
      ) : null}

      {props.variantSelectionLayout === VariantSelectionLayout.Cards &&
        includedProducts &&
        includedProducts.length > 0 && (
          <ProductDetailsContentAccordion title={props.translations['custom.breakdown'] || ''}>
            <ProductBreakdownAccordion translations={props.translations} />
            {selectedVariant?.standardEquipmentSpecification && selectedVariant?.standardEquipmentSpecification != '-' && (
              <S.StandardEquipmentContainer>
                <S.StandardEquipmentLabel>{props.translations['custom.standardequipment']}</S.StandardEquipmentLabel>
                <S.StandardEquipmentText
                  dangerouslySetInnerHTML={{ __html: selectedVariant.standardEquipmentSpecification }}
                ></S.StandardEquipmentText>
              </S.StandardEquipmentContainer>
            )}
          </ProductDetailsContentAccordion>
        )}

      <ProductDetailsContentAccordion>
        <S.OuterRepairabilityContainer>
          <S.WhereToBuy>
            <S.WhereToBuyCTA>
              <Click2Buy
                onInit={() => {
                  console.log(props.c2bAction);
                  setCtaState('active-c2b');
                }}
                onHide={() => setCtaState('active-sl')}
                text={translations['custom.click2buy.buttontext']}
                articleNumber={selectedVariant?.articleNumber}
                stackAdaptTrackingCode={props.translations['stackadapt.tracking.buynow']}
                c2bAction={props.c2bAction}
                onClick={() => {
                  const [item_category, item_category2, item_category3, item_category4, item_category5] =
                    categories ?? [];

                  windowCustomEvents.c2bClicked(props.product, selectedVariant);

                  gtm({
                    event: 'click_to_buy',
                    item_name: props.product.modelCode,
                    item_variant: selectedVariant?.name || '',
                    item_category,
                    item_category2,
                    item_category3,
                    item_category4,
                    item_category5,
                    item_id: selectedVariant?.articleNumber || '',
                  });
                }}
              />

              {ctaState === 'active-sl' || ctaState === 'unknown' ? (
                <Button
                  theme={!selectedVariant ? 'grey' : 'green'}
                  text={props.storeLocatorText}
                  icon="location"
                  iconPlacement="before"
                  onClick={(e) => {
                    if (!selectedVariant) {
                      e.stopPropagation();
                      e.preventDefault();

                      setInteractedWithStoreLocator(true);
                      return;
                    }

                    windowCustomEvents.storeLocatorClicked(props.product, selectedVariant);

                    pushConversion(props.translations['stackadapt.tracking.buynow']);
                    const [item_category, item_category2, item_category3, item_category4, item_category5] =
                      categories ?? [];
                    gtm({
                      event: 'click_to_store_locator',
                      item_name: props.product.modelCode,
                      item_variant: selectedVariant?.name || '',
                      item_category,
                      item_category2,
                      item_category3,
                      item_category4,
                      item_category5,
                      item_id: selectedVariant?.articleNumber || '',
                    });

                    // Changing this to updating the url directly will probably a bunch of the tracking above ^
                    window.open(props.storeLocatorUrl, '_blank');
                  }}
                ></Button>
              ) : (
                <S.StoreLocatorCTA
                  href={props.storeLocatorUrl}
                  onClick={(e) => {
                    e.preventDefault();
                    pushConversion(props.translations['stackadapt.tracking.buynow']);
                    const [item_category, item_category2, item_category3, item_category4, item_category5] =
                      categories ?? [];
                    gtm({
                      event: 'click_to_store_locator',
                      item_name: props.product.modelCode,
                      item_variant: selectedVariant?.name || '',
                      item_category,
                      item_category2,
                      item_category3,
                      item_category4,
                      item_category5,
                      item_id: selectedVariant?.articleNumber || '',
                    });

                    // Navigate to page
                    window.location.href = props.storeLocatorUrl;
                  }}
                >
                  <Pin /> {props.storeLocatorText}
                </S.StoreLocatorCTA>
              )}

              {interactedWithStoreLocator ? (
                <S.WhereToBuyNoticeWrapper>
                  <S.WhereToBuyNotice>
                    <Warning />
                    <span>{props.translations['custom.variantselector.wheretobuynotice']}</span>
                  </S.WhereToBuyNotice>
                </S.WhereToBuyNoticeWrapper>
              ) : null}

              {props.rangeCta && props.rangeCta.rangeUrl && (
                <Link text={props.rangeCta.rangeText} href={props.rangeCta.rangeUrl} theme="black-grey" icon="arrow" />
              )}
              {props.formCta && props.formCta.openForm && (
                <Button
                  text={props.formCta.rangeText}
                  onClick={() => setIsOpen(true)}
                  theme="black-grey"
                  icon="arrow"
                />
              )}
            </S.WhereToBuyCTA>
          </S.WhereToBuy>
          {props.repairabilityIndexImageUrl && (
            <S.RepairabilityContainer
              {...(props.repairabilityIndexLinkUrl
                ? {
                    as: 'a',
                    target: '_blank',
                    href: props.repairabilityIndexLinkUrl,
                  }
                : {})}
            >
              <S.RepairabilityImage src={props.repairabilityIndexImageUrl} />
            </S.RepairabilityContainer>
          )}
        </S.OuterRepairabilityContainer>
      </ProductDetailsContentAccordion>

      {(icons?.features && icons.features.length > 0) ||
      (icons?.standards && icons.standards.length > 0) ||
      (icons?.care && icons?.care.length > 0) ||
      (documents && documents.length > 0) ? (
        <ProductDetailsContentAccordion>
          <S.Section>
            <S.Tabs>
              {icons?.features && icons.features.length > 0 && (
                <S.Tab
                  title={props.translations['custom.productdetail.tabs.features']}
                  type="button"
                  isActive={iconTab === 0}
                  onClick={() => handleTabClick(0)}
                >
                  {props.translations['custom.productdetail.tabs.features']}
                </S.Tab>
              )}

              {icons?.standards && icons.standards.length > 0 && (
                <S.Tab
                  title={props.translations['custom.productdetail.tabs.standards']}
                  type="button"
                  isActive={iconTab === 3}
                  onClick={() => handleTabClick(3)}
                >
                  {props.translations['custom.productdetail.tabs.standards']}
                </S.Tab>
              )}

              {icons?.care && icons?.care.length > 0 && (
                <S.Tab
                  title={props.translations['custom.productdetail.tabs.careicons']}
                  type="button"
                  isActive={iconTab === 1}
                  onClick={() => handleTabClick(1)}
                >
                  {props.translations['custom.productdetail.tabs.careicons']}
                </S.Tab>
              )}

              {documents && documents.length > 0 && (
                <S.Tab
                  title={props.translations['custom.productdetail.tabs.documents']}
                  type="button"
                  isActive={iconTab === 2}
                  onClick={() => handleTabClick(2)}
                >
                  {props.translations['custom.productdetail.tabs.documents']}
                </S.Tab>
              )}
            </S.Tabs>

            {icons?.features && icons.features.length > 0 && (
              <S.TabContent isActive={iconTab === 0}>
                <ProductStandardsFeatures
                  tab={iconTab}
                  hideTitleOnMobile={false}
                  linkText={props.translations['custom.productdetail.safetyfeatures.features.linktext']}
                  linkUrl={props.featuresUrl}
                  icons={icons?.features}
                />
              </S.TabContent>
            )}
            {icons?.standards && icons.standards.length > 0 && (
              <S.TabContent isActive={iconTab === 3}>
                <ProductStandardsFeatures
                  tab={iconTab}
                  hideTitleOnMobile={false}
                  linkText={props.translations['custom.productdetail.safetyfeatures.standards.linktext']}
                  linkUrl={props.standardsUrl}
                  icons={icons?.standards}
                />
              </S.TabContent>
            )}

            {documents && documents.length > 0 && (
              <S.TabContent isActive={iconTab === 2}>
                <ProductDocuments
                  documents={[
                    {
                      title: undefined,
                      documents: documents
                        .filter((x) => typeof x.url === 'string')
                        .map((x) => ({
                          name: x.title,
                          linkUrl: x.url as string,
                          itemName: props.product.modelCode,
                          itemVariant: selectedVariant?.agilityId?.toString(),
                        })),
                    },
                  ]}
                />
              </S.TabContent>
            )}

            {icons?.care && icons.care.length > 0 && (
              <S.TabContent isActive={iconTab === 1}>
                <ProductStandardsFeatures tab={iconTab} hideTitleOnMobile={true} icons={icons?.care} />
              </S.TabContent>
            )}
          </S.Section>
        </ProductDetailsContentAccordion>
      ) : null}

      {(props.technologies && props.technologies.length > 0) ||
      (props.customIcons && props.customIcons.length > 0) ||
      (props.recyclingIcon && props.recyclingIcon.length > 0 && props.recyclingIconLink) ||
      (props.warrantyLogo && props.warrantyLogo.length > 0) ? (
        <ProductDetailsContentAccordion
          title={
            (props.warrantyLogo && props.warrantyLogo.length > 0) || (props.customIcons && props.customIcons.length > 0)
              ? ''
              : props.translations['custom.technology'] || ''
          }
        >
          <S.Technologies>
            {props.technologies &&
              props.technologies.length > 0 &&
              props.technologies.map((x) => (
                <>
                  {x.url ? (
                    <S.TechnologyLink href={x.url} target={x.openInNewTab ? '_blank' : '_self'}>
                      <img
                        loading="lazy"
                        alt={x.description || ''}
                        src={new ImageTransform(x.imageUrl).width(306).url}
                      />
                    </S.TechnologyLink>
                  ) : (
                    <S.TechnologyItem key={x.imageUrl}>
                      <img
                        loading="lazy"
                        alt={x.description || ''}
                        src={new ImageTransform(x.imageUrl).width(306).url}
                      />
                    </S.TechnologyItem>
                  )}
                </>
              ))}
            {props.recyclingIcon &&
              props.recyclingIconLink &&
              props.recyclingIcon.map((icon) => (
                <S.RecyclingLink key={icon} href={props.recyclingIconLink} target="_blank">
                  <S.RecyclingImg loading="lazy" src={new ImageTransform(getRecyclingIcon(icon)).width(102).url} />
                </S.RecyclingLink>
              ))}
            {props.warrantyLogo &&
              props.warrantyLogo.map((icon) => (
                <>
                  {icon.url ? (
                    <S.WarrantyLink key={icon.description} href={icon.url} target="_blank">
                      <S.WarrantyImg
                        loading="lazy"
                        alt={icon.description || ''}
                        src={new ImageTransform(icon.imageUrl).width(150).url}
                      />
                    </S.WarrantyLink>
                  ) : (
                    <S.WarrantyItem key={icon.description}>
                      <S.WarrantyImg
                        loading="lazy"
                        alt={icon.description || ''}
                        src={new ImageTransform(icon.imageUrl).width(150).url}
                      />
                    </S.WarrantyItem>
                  )}
                </>
              ))}
            {props.customIcons &&
              props.customIcons.length > 0 &&
              props.customIcons.map((x) => (
                <>
                  {x.url ? (
                    <S.TechnologyLink href={x.url} target={x.openInNewTab ? '_blank' : '_self'}>
                      <img
                        loading="lazy"
                        alt={x.description || ''}
                        src={new ImageTransform(x.imageUrl).width(306).url}
                      />
                    </S.TechnologyLink>
                  ) : (
                    <S.TechnologyItem key={x.imageUrl}>
                      <img
                        loading="lazy"
                        alt={x.description || ''}
                        src={new ImageTransform(x.imageUrl).width(306).url}
                      />
                    </S.TechnologyItem>
                  )}
                </>
              ))}
          </S.Technologies>
        </ProductDetailsContentAccordion>
      ) : null}

      {icons?.applications && icons.applications.length > 0 && (
        <ProductDetailsContentAccordion title={props.translations['custom.productfeatureapplications.title']}>
          <S.Applications>
            {icons.applications.map((application, i) => (
              <S.Application key={`Application_${i}`}>
                <S.ApplicationImage src={application.iconUrl} alt={application.iconText} />
                <S.ApplicationName>{application.iconText}</S.ApplicationName>
              </S.Application>
            ))}
          </S.Applications>
        </ProductDetailsContentAccordion>
      )}

      {promotions && promotions.length > 0 && (
        <ProductDetailsContentAccordion title={props.translations['custom.productdetail.promotions']}>
          {promotions.map((promotion, i) => (
            <S.Promotion key={`Promotion_${i}`}>
              <S.PromotionImageContainer>
                <S.PromotionImage src={promotion.promotionImageUrl} alt={promotion.promotionName} />
              </S.PromotionImageContainer>
              <S.PromotionContentContainer>
                <S.PromotionTitle>{promotion.promotionName}</S.PromotionTitle>
                <S.PromotionDescription>{promotion.promotionDescription}</S.PromotionDescription>
                {promotion.promotionButtonText && promotion.promotionButtonLink && (
                  <S.PromotionButtonContainer>
                    <Link
                      theme="red"
                      text={promotion.promotionButtonText}
                      href={promotion.promotionButtonLink}
                      size="small"
                    />
                  </S.PromotionButtonContainer>
                )}
              </S.PromotionContentContainer>
            </S.Promotion>
          ))}
        </ProductDetailsContentAccordion>
      )}

      <LasersPopupForm
        {...props.popupData}
        pageContext={props.pageContext}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        categories={categories || []}
        productVariant={selectedVariant}
      />
    </S.Container>
  );
};

export default withRedux(ProductDetailsContent);
